import React from 'react';
import styled from 'styled-components';

const FooterSection = styled.footer`
  padding: ${p => p.theme.spacing.xxl} ${p => p.theme.spacing.lg} ${p => p.theme.spacing.lg};
  background: ${p => p.theme.colors.background};
  border-top: 1px solid ${p => p.theme.colors.borderSubtle};
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  gap: ${p => p.theme.spacing.xl};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: ${p => p.theme.spacing.lg};
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${p => p.theme.spacing.md};
  }
`;

const Brand = styled.div``;

const Logo = styled.img`
  height: 32px;
  width: auto;
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const Tagline = styled.p`
  font-size: 0.875rem;
  color: ${p => p.theme.colors.subtextGray};
  line-height: 1.6;
  max-width: 280px;
  margin: 0 0 ${p => p.theme.spacing.sm};
`;

const SocialRow = styled.div`
  display: flex;
  gap: 10px;
`;

const SocialLink = styled.a`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: ${p => p.theme.colors.surfaceCard};
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  color: ${p => p.theme.colors.textDark};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${p => p.theme.colors.accentPurple};
    color: ${p => p.theme.colors.accentPurple};
    transform: translateY(-2px);
  }
`;

const Column = styled.div``;

const ColumnTitle = styled.h4`
  font-size: 0.75rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.textLight};
  margin: 0 0 ${p => p.theme.spacing.sm};
  font-weight: ${p => p.theme.typography.weights.semibold};
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FooterLink = styled.a`
  font-size: 0.875rem;
  color: ${p => p.theme.colors.subtextGray};
  text-decoration: none;
  transition: color 0.2s ease;
  cursor: pointer;

  &:hover {
    color: ${p => p.theme.colors.accentPurple};
  }
`;

const BottomBar = styled.div`
  max-width: 1200px;
  margin: ${p => p.theme.spacing.xl} auto 0;
  padding-top: ${p => p.theme.spacing.md};
  border-top: 1px solid ${p => p.theme.colors.borderSubtle};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: 0.8125rem;
  color: ${p => p.theme.colors.subtextGray};
  margin: 0;
`;

const BottomLinks = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;
`;

/* ── Social icons ── */
const TwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <FooterSection>
      <Container>
        <Brand>
          <Logo src="/logo with company name horizontal.png" alt="Continua Labs" />
          <Tagline>
            The connected platform for continuous improvement teams. Learn, plan, execute, and improve — all in one workspace.
          </Tagline>
          <SocialRow>
            <SocialLink href="https://x.com/continualabs" target="_blank" rel="noopener noreferrer" aria-label="X">
              <TwitterIcon />
            </SocialLink>
            <SocialLink href="https://www.linkedin.com/company/continualabs" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <LinkedInIcon />
            </SocialLink>
          </SocialRow>
        </Brand>

        <Column>
          <ColumnTitle>Product</ColumnTitle>
          <LinkList>
            <li><FooterLink href="#tools">Tools</FooterLink></li>
            <li><FooterLink href="#how-it-works">How it works</FooterLink></li>
            <li><FooterLink href="#credentials">Credentials</FooterLink></li>
            <li><FooterLink href="https://continualabsdashboardd.netlify.app/login" target="_blank" rel="noopener noreferrer">Get Started</FooterLink></li>
          </LinkList>
        </Column>

        <Column>
          <ColumnTitle>Company</ColumnTitle>
          <LinkList>
            <li><FooterLink href="https://continualabs.com" target="_blank" rel="noopener noreferrer">About</FooterLink></li>
            <li><FooterLink href="#blog">Blog</FooterLink></li>
            <li><FooterLink href="mailto:hello@continualabs.com">Contact</FooterLink></li>
          </LinkList>
        </Column>

        <Column>
          <ColumnTitle>Account</ColumnTitle>
          <LinkList>
            <li><FooterLink href="https://continualabsdashboardd.netlify.app/login" target="_blank" rel="noopener noreferrer">Sign in</FooterLink></li>
            <li><FooterLink href="https://continualabsdashboardd.netlify.app/login" target="_blank" rel="noopener noreferrer">Sign up free</FooterLink></li>
          </LinkList>
        </Column>
      </Container>

      <BottomBar>
        <Copyright>© {year} Continua Labs. All rights reserved.</Copyright>
        <BottomLinks>
          <FooterLink href="https://continualabs.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</FooterLink>
          <FooterLink href="https://continualabs.com/terms" target="_blank" rel="noopener noreferrer">Terms</FooterLink>
        </BottomLinks>
      </BottomBar>
    </FooterSection>
  );
};

export default Footer;
